import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { clearActingUserId, saveActingUserId } from '../../auth/actingUser'
import { activateProxyPersona, deactivateProxyPersona } from './api/proxyPersonaApi'
import { clearAppFeedbackToken } from '../../auth/appFeedbackSession'
import { dismissAppFeedbackWidget } from '../../lib/appFeedback/loader'
import type { MeResponse } from '../../auth/useCurrentUser'
import {
  beginPersonaSwitch,
  cancelPersonaSwitch,
  markPersonaSwitchNavigating,
} from './personaSwitchState'

/**
 * Persona geçişi/çıkışı ve geçersiz vekaletten kurtarma (089).
 *
 * <p>Üç akış da **aynı** üç adımı paylaşır ve bu yüzden tek yerde durur: çerezi güncelle → tüm
 * sunucu-durumu önbelleğini geçersiz kıl → anasayfaya git. Kopyalansaydı, biri güncellenip diğeri
 * unutulduğunda kullanıcı eski kimliğin verisini yeni kimlikle görmeye devam ederdi.
 */
export function useProxySwitch() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  /**
   * Persona değişiminin ortak sonu.
   *
   * Önbellek **tümden** temizlenir (tek tek anahtar seçilmez): kimlik değiştiğinde ekrandaki her
   * sorgunun kapsamı değişir ve yarın eklenecek bir liste ekranının anahtarını burada hatırlamak
   * zorunda kalmak, kaçırıldığında sessiz bir veri sızıntısı üretirdi.
   *
   * Anasayfaya yönlendirme zorunludur: yeni kimliğin yetkisi ya da verisi olmayan bir ekranda
   * kalmak 403/404 üretirdi.
   *
   * @param switchId `beginPersonaSwitch`'in verdiği geçiş numarası.
   * @param nextMe Sunucunun geçiş yanıtında döndüğü yeni etkin kimlik; verilirse `me` ondan yazılır
   *     ve ayrıca `GET /me` turu atılmaz.
   */
  const finishSwitch = useCallback(
    async (switchId: number, nextMe?: MeResponse) => {
      // ÖNCE iptal, SONRA geçersiz kılma — `clearAppSession` ile aynı sıra ve aynı gerekçe: uçuşta olan
      // bir istek eski persona başlığıyla gittiği için, sonradan dönüp YENİ personanın önbelleğine
      // ÖNCEKİ kişinin verisini yazabilir. Pasif (unmounted) sorgular `invalidateQueries` tarafından
      // yeniden başlatılmadığı için bu risk yalnız iptalle kapanır.
      await queryClient.cancelQueries()
      // Ekranın sorguları SİLİNİR, bayat işaretlemek yetmez (kullanıcı bildirimi, 2026-10-13):
      // yeniden kurulan ekran bayat veriyi taze çekerken ÖNCE onu gösterir. Anasayfa önceki kimliğin
      // kapsamını ("Yönetici Raporu aç") ve pano verisini önbellekte bulup bir an onu çiziyor, yeni
      // kimliğin kapsamı gelince Raporum'a geçiyordu — önceki personanın ekranı, bu kez önbellekten.
      //
      // `inactive` = içerik ağacının sorguları: ağaç geçişin başında söküldüğü için (`runSwitch`)
      // bu noktada gözlemcisi kalmayan her sorgu ekrana aittir. Kabuğun sorguları (`me`, bildirim
      // sayısı, persona listesi) aktiftir ve SİLİNMEZ — `me` silinseydi kabuk bir an "kullanıcı yok"
      // durumuna düşerdi; onlar aşağıda bayat işaretlenir ve `me` ayrıca tazelenir.
      queryClient.removeQueries({ type: 'inactive' })
      // `refetchType: 'none'` ZORUNLU (2026-08-26): çıplak `invalidateQueries()` yalnız bayat
      // işaretlemez, AKTİF sorguları hemen yeniden çeker. Yönlendirme React'in commit'inden önce
      // gerçekleşmediği için o an eski ekran hâlâ ayakta: sorguları YENİ persona başlığıyla tekrar
      // gidiyor, yetkisi olmayan uçlar 403 dönüyor ve kullanıcı anasayfaya düşerken "bu işlem için
      // yetkiniz yok" toast'ları görüyordu (kullanıcı bildirimi: admin ekranından yetkisiz bir
      // personaya geçiş). Sıralamayı değiştirmek çözmez — `navigate` bir state güncellemesidir ve aynı
      // microtask'ta commit edilmiş olması garanti değildir.
      //
      // Tazelik KAYBEDİLMEZ: `AppLayout` içerik ağacını seçili personayla anahtarlıyor, yani kimlik
      // değişince ağaç sökülüp yeniden kurulur ve bayat sorgular mount'ta taze çekilir. Aynı sayfada
      // kalınsa bile (geçişi anasayfada yapmak) anahtar değiştiği için bu geçerlidir.
      await queryClient.invalidateQueries({ refetchType: 'none' })
      // `me` AÇIKÇA yeniden çekilir (bağımsız code review bulgusu, 2026-08-26). Kabuk — uyarı şeridi,
      // kimlik menüsü, sol menü — anahtarın DIŞINDA olduğu için sökülmez; `staleTime: Infinity` ile
      // birlikte, yalnız bayat işaretlemek onları eski kimlikte bırakırdı. Şerit önceki personanın
      // adını yazar, sol menü önceki personanın öğelerini listeler ve oradan bir tıklama rota
      // korumasını (aynı bayat `me`) geçip uçtan 403 alır — bu düzeltmenin kaldırdığı toast'ın aynısı.
      //
      // Eskiden bu kendiliğinden düzeliyordu ama YALNIZ tesadüfen: anahtarlanan ağacın içindeki bir
      // bileşen (anasayfada `useGrants`) `me`'ye yeniden abone olduğu için. O çağrı kalkarsa şerit
      // süresiz olarak yanlış kimliği gösterirdi. Tek istek, hem pencereyi hem örtük bağımlılığı kapatır.
      //
      // Geçiş yanıtı yeni kimliği zaten taşıyorsa istek de atılmaz (2026-10-13): `POST /active`'in
      // gövdesi, `/me`'nin aynı persona başlığıyla döneceği yanıtın birebir aynısıdır (ikisi de
      // `resolveTargetPersona(...).actingAs(realUser)`). İkinci tur, geçişin süresini ikiye katlıyordu.
      // `setQueryData` iptal ve geçersiz kılmadan SONRA çalışır: önce yazılsaydı, uçuştaki eski bir
      // `me` isteği iptalle geri sarılır ya da bayat işaretlenirdi.
      if (nextMe) {
        queryClient.setQueryData(['me'], nextMe)
      } else {
        await queryClient.refetchQueries({ queryKey: ['me'] })
      }

      // THY AppFeedback (096): persona geçişi TAM SAYFA YENİLEMESİ yapmaz — açık bir anket ekranda
      // kalır ve gönderim artık başkasının kimliğiyle gider. Bellekteki belirteç de eski kimliğe
      // aittir. İkisi de burada düşürülür (FR-010, FR-014).
      dismissAppFeedbackWidget()
      clearAppFeedbackToken()

      // İçerik, yönlendirme commit edilene kadar gizli KALIR — bayrağı burada kapatmak yanlış olurdu
      // (gerekçe `personaSwitchState`'te); kapanışı `AppLayout` yapar.
      markPersonaSwitchNavigating(switchId)
      navigate('/', { replace: true })
    },
    [navigate, queryClient],
  )

  /**
   * Geçişin ortak çerçevesi: içerik İLK ADIMDA ekrandan kalkar, başarısızlıkta geri gelir.
   *
   * <p>Bu, sunucuya gitmeden önce yapılır, çünkü bekleme süresinin tamamı sunucu turlarıdır ve o
   * süre boyunca ekran önceki kimliğin verisini gösteriyordu (kullanıcı bildirimi, 2026-10-13).
   * Neden gizlenmek yerine söküldüğü `AppLayout`'ta.
   *
   * <p>Geçiş numarası adımlara taşınır: üst üste binen bir geçişin iptali, sürmekte olanın
   * bayrağını kapatmasın (gerekçe `personaSwitchState`'te).
   *
   * <p>Adımlardan önce bir makro görev beklenir: `finishSwitch` pasif sorguları "ekrana ait" sayıp
   * siler ve bu, içerik ağacının o an gerçekten SÖKÜLMÜŞ olmasına dayanır. Bayrağın tetiklediği
   * render React'te bir mikro görevde işlenir; giriş/çıkışta önce sunucu beklendiği için bu zaten
   * hep olmuştur, ama sunucuya gitmeyen kurtarma akışında doğrudan `finishSwitch`'e girilir. Bekleme
   * yoksa ekranın sorguları o an hâlâ aktif olabilir, silinmez ve yeniden kurulan ekran önceki
   * kimliğin verisini gösterirdi.
   */
  const runSwitch = useCallback(async (steps: (switchId: number) => Promise<void>) => {
    const switchId = beginPersonaSwitch()
    try {
      await new Promise<void>((resolve) => setTimeout(resolve, 0))
      await steps(switchId)
    } catch (error) {
      cancelPersonaSwitch(switchId)
      throw error
    }
  }, [])

  /** Vekili olunan bir kişinin personasına geçer. */
  const enterPersona = useCallback(
    (userId: number) =>
      runSwitch(async (switchId) => {
        // Sunucu önce doğrular: geçersizse hata fırlar ve çerez HİÇ yazılmaz — kullanıcı,
        // kullanamayacağı bir personaya geçmiş gibi görünmez.
        const nextMe = await activateProxyPersona(userId)
        saveActingUserId(userId)
        await finishSwitch(switchId, nextMe)
      }),
    [finishSwitch, runSwitch],
  )

  /**
   * Kendi kimliğine döner.
   *
   * <p>`DELETE /active`'in yanıtı `me`'ye **yazılmaz**, `/me` yeniden çekilir: o yanıt
   * `resolveSelfPersona` ile kurulur ve normal `/me` yolunun **tek** adımını atlar — HR erişim
   * kapısı (`accessRestricted`). HR grup kodundan gelen yetkiler de atlanıyordu ve bu bir hataydı;
   * 22.10.2026'da düzeltildi (sunucu tarafı), ama yeniden çekme kuralı DURUYOR: kapı hâlâ
   * uygulanmıyor, yani yanıt kısıtlı bir kullanıcıyı kısıtsız gösterirdi.
   */
  const exitPersona = useCallback(
    () =>
      runSwitch(async (switchId) => {
        await deactivateProxyPersona()
        clearActingUserId()
        await finishSwitch(switchId)
      }),
    [finishSwitch, runSwitch],
  )

  /**
   * Vekalet çalışma sırasında geçersizleşti (sunucu `PROXY_NOT_ACTIVE` döndü).
   *
   * Sunucuya **gidilmez**: istek zaten reddedildi ve kullanıcının vekaleti artık yok — bir çıkış
   * çağrısı da aynı nedenle anlamsız olurdu. Yerelde kendi personasına düşülür.
   */
  const recoverFromInvalidPersona = useCallback(
    () =>
      runSwitch(async (switchId) => {
        clearActingUserId()
        await finishSwitch(switchId)
      }),
    [finishSwitch, runSwitch],
  )

  return { enterPersona, exitPersona, recoverFromInvalidPersona }
}
